(ns lupapalvelu.dev.action-request
  "Developer tool for executing and tracking queries/commands."
  (:require [cljs.pprint :as pprint]
            [cljs.reader :as reader]
            [goog.crypt.hash32 :as hash32]
            [lupapalvelu.data :as data]
            [lupapalvelu.ui.misc]
            [re-frame.core :as rf]
            [sade.strings :as ss]))

(def datapath (partial data/datapath ::state))
(def set-d    (partial data/set-d ::state))
(def get-d    (partial data/get-d ::state))

(defn- update-state [db f & args]
  (apply update-in db (datapath) f args))

(def ^:private default-state
  {:type                "query"
   :action              ""
   :params              "{}"
   :params-format       "json"
   :result-format       "json"
   :track-page-actions? false
   :selected-list-tab   "history"
   :result              ""
   :result-value        nil
   :history             []
   :tracked-actions     []
   :duration-ms         nil
   :started-at-ms       nil
   :pending?            false
   :last-error          nil})

(def request-history-storage-key "lupapalvelu.dev.action-request.history")
(def max-request-history-size 25)
(defonce ^:private lpajax-action-tracker (atom nil))
(defonce ^:private suppressed-page-action-tracks (atom 0))

(defn- limit-request-history [history]
  (vec (take max-request-history-size history)))

(defn- read-request-history []
  (try
    (if-let [stored (.getItem js/localStorage request-history-storage-key)]
      (limit-request-history (reader/read-string stored))
      [])
    (catch :default e
      (.warn js/console "Could not read action request history." e)
      [])))

(defn- persist-request-history! [history]
  (try
    (.setItem js/localStorage
              request-history-storage-key
              (pr-str history))
    (catch :default e
      (.warn js/console "Could not store action request history." e))))

(rf/reg-fx ::persist-request-history
  (fn [history]
    (persist-request-history! history)))

(rf/reg-cofx ::local-storage-history
  (fn [cofx _]
    (assoc cofx ::local-storage-history (read-request-history))))

(defn- event-value [e]
  (.. e -target -value))

(defn- now-ms []
  (.now js/Date))

(defn- elapsed-ms [started-at-ms]
  (max 0 (- (now-ms) started-at-ms)))

(defn- format-duration [duration-ms]
  (if (< duration-ms 1000)
    (str (js/Math.round duration-ms) " ms")
    (str (.toFixed (/ duration-ms 1000) 2) " s")))

(defn- stable-value [value]
  (let [compare-keys (fn [x y]
                       (let [c (compare (pr-str x) (pr-str y))]
                         (if (zero? c)
                           (compare (hash x) (hash y))
                           c)))]
    (cond
      (map? value)    (into (sorted-map-by compare-keys)
                            (map (fn [[k v]]
                                   [k (stable-value v)])
                                 value))
      (vector? value) (mapv stable-value value)
      (set? value)    (->> value
                           (map stable-value)
                           (sort-by pr-str)
                           vec)
      (seq? value)    (doall (map stable-value value))
      :else           value)))

(defn- string-hash [^string s]
  (.toString (hash32/encodeString s) 36))

(defn- request-hash [type action params]
  (-> {:type   type
       :action action
       :params (stable-value params)}
      pr-str
      string-hash))

(defn- build-history-entry [type action params params-format timestamp]
  {:hash          (request-hash type action params)
   :timestamp     timestamp
   :type          type
   :action        action
   :params        params
   :params-format params-format})

(defn- build-tracking-entry [type action params timestamp]
  {:tracking-id   (str timestamp "-" (random-uuid))
   :hash          (request-hash type action params)
   :timestamp     timestamp
   :type          type
   :action        action
   :params        params
   :params-format "cljs"})

(defn- add-request-history [history entry]
  (let [same-entry? #(= (:hash %) (:hash entry))
        entry'      (if-let [existing-entry (some #(when (same-entry? %) %) history)]
                      (assoc existing-entry :timestamp (:timestamp entry))
                      entry)]
    (limit-request-history
      (into [entry']
            (remove same-entry? history)))))

(defn- add-tracked-action [tracked-actions entry]
  (into [entry] tracked-actions))

(defn- action-name->string [action]
  (cond
    (keyword? action) (name action)
    (some? action)    (str action)
    :else             ""))

(defn- js-params->clj [params]
  (if (some? params)
    (js->clj params :keywordize-keys true)
    {}))

(defn- consume-suppressed-page-action-track! []
  (let [suppressed? (pos? @suppressed-page-action-tracks)]
    (when suppressed?
      (swap! suppressed-page-action-tracks dec))
    suppressed?))

(defn- trackable-action-call [type original-fn action params]
  (let [call          (.call original-fn js/lpAjax action params)
        original-call (when call (.-call call))]
    (when original-call
      (set! (.-call call)
            (fn [& args]
              (when-not (consume-suppressed-page-action-track!)
                (rf/dispatch [::track-page-action type action params (now-ms)]))
              (.apply original-call call (to-array args)))))
    call))

(defn- install-page-action-tracker! []
  (when-not @lpajax-action-tracker
    (let [query-fn   (.-query js/lpAjax)
          command-fn (.-command js/lpAjax)]
      (reset! lpajax-action-tracker {:query   query-fn
                                     :command command-fn})
      ;; TODO: Also track :lp/fetch requests
      (set! (.-query js/lpAjax)
            (fn [action params]
              (trackable-action-call "query" query-fn action params)))
      (set! (.-command js/lpAjax)
            (fn [action params]
              (trackable-action-call "command" command-fn action params))))))

(defn- uninstall-page-action-tracker! []
  (when-let [{:keys [query command]} @lpajax-action-tracker]
    (set! (.-query js/lpAjax) query)
    (set! (.-command js/lpAjax) command)
    (reset! lpajax-action-tracker nil)))

(rf/reg-fx ::set-page-action-tracking
  (fn [enabled?]
    (if enabled?
      (install-page-action-tracker!)
      (uninstall-page-action-tracker!))))

(rf/reg-fx ::suppress-next-page-action-track
  (fn [count]
    (swap! suppressed-page-action-tracks + (or count 1))))

(defn- parse-json [s]
  (js->clj (.parse js/JSON s) :keywordize-keys true))

(defn- parse-cljs [s]
  (reader/read-string s))

(defn- parse-params-with-format [s]
  (let [trimmed (ss/trim s)]
    (if (ss/blank? trimmed)
      {:format "json"
       :value  {}}
      (try
        {:format "json"
         :value  (parse-json trimmed)}
        (catch :default json-error
          (try
            {:format "cljs"
             :value  (parse-cljs trimmed)}
            (catch :default cljs-error
              (throw (js/Error. (str "Could not parse parameters as JSON or CLJS.\n\n"
                                     "JSON: " (.-message json-error) "\n"
                                     "CLJS: " (.-message cljs-error)))))))))))

(defn- parse-params [s]
  (:value (parse-params-with-format s)))

(defn- detect-params-format [s]
  (try
    (:format (parse-params-with-format s))
    (catch :default _
      nil)))

(defn- clj->pretty-cljs [value]
  (with-out-str
    (pprint/pprint value)))

(defn- clj->pretty-json [value]
  (.stringify js/JSON (clj->js value) nil 2))

(defn- format-params-string [s]
  (let [trimmed (ss/trim s)]
    (if (ss/blank? trimmed)
      "{}"
      (let [{:keys [format value]} (parse-params-with-format trimmed)]
        (case format
          "cljs" (clj->pretty-cljs value)
          (clj->pretty-json value))))))

(defn- params->format [params-format params]
  (case params-format
    "cljs" (clj->pretty-cljs params)
    (clj->pretty-json params)))

(defn- result->string [result-format result]
  (cond
    (string? result) result
    (= result-format "cljs") (clj->pretty-cljs result)
    :else (try
            (clj->pretty-json result)
            (catch :default _
              (clj->pretty-cljs result)))))

(defn- visible-result [{:keys [result result-format result-value]}]
  (if (some? result-value)
    (result->string result-format result-value)
    result))

(defn- fail-message
  ([failure]
   (str "Request failed\n\n" (result->string "cljs" failure))))

(defn- request-effect [type action params started-at-ms]
  (let [success [::request-success started-at-ms]
        error   [::request-error started-at-ms]]
    (case type
      "command" [:action/command {:name    (keyword action)
                                  :params  params
                                  :success success
                                  :error   error
                                  :fail    [::request-failure started-at-ms]}]
      "query"   [:action/query {:name    (keyword action)
                                :params  params
                                :success success
                                :error   error}])))

(rf/reg-event-fx ::init
  [(rf/inject-cofx ::local-storage-history)]
  (fn [{db :db ::keys [local-storage-history]} _]
    {:db (if-let [state (get-d db)]
           (set-d db (merge default-state state))
           (set-d db (assoc default-state :history local-storage-history)))}))

(rf/reg-sub ::state
  (fn [db _] (get-d db)))

(rf/reg-sub ::type
  :<- [::state]
  :-> :type)

(rf/reg-sub ::action
  :<- [::state]
  :-> :action)

(rf/reg-sub ::params
  :<- [::state]
  :-> :params)

(rf/reg-sub ::params-format
  :<- [::state]
  :-> :params-format)

(rf/reg-sub ::result-format
  :<- [::state]
  :-> :result-format)

(rf/reg-sub ::track-page-actions?
  :<- [::state]
  :-> :track-page-actions?)

(rf/reg-sub ::selected-list-tab
  :<- [::state]
  :-> :selected-list-tab)

(rf/reg-sub ::pending?
  :<- [::state]
  :-> :pending?)

(rf/reg-sub ::history
  :<- [::state]
  :-> :history)

(rf/reg-sub ::tracked-actions
  :<- [::state]
  :-> :tracked-actions)

(rf/reg-event-db ::set-type
  (fn [db [_ type]]
    (set-d db :type type)))

(rf/reg-event-db ::set-action
  (fn [db [_ action]]
    (set-d db :action action)))

(rf/reg-event-db ::set-params
  (fn [db [_ params]]
    (set-d db :params params)))

(rf/reg-event-db ::detect-params-format
  (fn [db _]
    (if-let [detected (detect-params-format (get-d db :params))]
      (set-d db :params-format detected)
      db)))

(rf/reg-event-fx ::format-params
  (fn [{db :db} _]
    (try
      (let [params' (format-params-string (get-d db :params))
            format' (detect-params-format params')]
        {:db (update-state db assoc
                           :params params'
                           :params-format format')})
      (catch :default e
        {:db (update-state db assoc
                           :last-error e
                           :result-value nil
                           :result (.-message e))}))))

(rf/reg-event-db ::set-result-format
  (fn [db [_ result-format]]
    (set-d db :result-format result-format)))

(rf/reg-event-db ::set-selected-list-tab
  (fn [db [_ tab]]
    (set-d db :selected-list-tab tab)))

(rf/reg-event-fx ::set-track-page-actions
  (fn [{db :db} [_ enabled?]]
    {:db (cond-> (set-d db :track-page-actions? enabled?)
           enabled? (set-d :selected-list-tab "tracking"))
     :fx [[::set-page-action-tracking enabled?]]}))

(rf/reg-event-fx ::convert-params
  (fn [{db :db} [_ params-format]]
    (try
      (let [params (parse-params (get-d db :params))]
        {:db (update-state db assoc
                           :params-format params-format
                           :params (params->format params-format params))})
      (catch :default e
        {:db (update-state db assoc
                           :last-error e
                           :result-value nil
                           :result (.-message e))}))))

(rf/reg-event-fx ::restore-request
  (fn [{db :db} [_ {:keys [type action params params-format]}]]
    (let [params-format (or params-format (get-d db :params-format) "json")]
      {:db (update-state db assoc
                         :type type
                         :action action
                         :params-format params-format
                         :params (params->format params-format params))})))

(rf/reg-event-fx ::clear-history
  (fn [{db :db} _]
    {:db (set-d db :history [])
     :fx [[::persist-request-history []]]}))

(rf/reg-event-db ::clear-tracked-actions
  (fn [db _]
    (set-d db :tracked-actions [])))

(rf/reg-event-fx ::track-page-action
  (fn [{db :db} [_ type action params timestamp]]
    (when (get-d db :track-page-actions?)
      (let [entry (build-tracking-entry type
                                        (action-name->string action)
                                        (js-params->clj params)
                                        timestamp)]
        {:db (set-d db :tracked-actions (add-tracked-action (get-d db :tracked-actions) entry))}))))

(rf/reg-event-fx ::execute-request
  (fn [{db :db} _]
    (when-not (get-d db :pending?)
      (let [{:keys [type action params]} (get-d db)
            action                       (ss/trim action)]
        (try
          (when (ss/blank? action)
            (throw (js/Error. "Action name is required.")))
          (let [{params-format :format
                 params        :value} (parse-params-with-format params)
                _                      (when-not (map? params)
                                         (throw (js/Error. "Parameters must be a JSON or CLJS map/object.")))
                params                 (into {} (map (fn [[k v]] [(keyword k) v]) params))
                started-at-ms          (now-ms)
                history'               (add-request-history (get-d db :history)
                                                            (build-history-entry type action params params-format started-at-ms))]
            {:db (update-state db assoc
                               :pending? true
                               :started-at-ms started-at-ms
                               :duration-ms nil
                               :history history'
                               :last-error nil
                               :result "Waiting for response..."
                               :result-value nil)
             :fx [[::suppress-next-page-action-track 1]
                  (request-effect type action params started-at-ms)
                  [::persist-request-history history']]})
          (catch :default e
            {:db (update-state db assoc
                               :pending? false
                               :started-at-ms nil
                               :duration-ms nil
                               :last-error e
                               :result-value nil
                               :result (.-message e))}))))))

(rf/reg-event-fx ::request-success
  (fn [{db :db} [_ result started-at-ms]]
    {:db (update-state db assoc
                       :pending? false
                       :started-at-ms nil
                       :duration-ms (elapsed-ms started-at-ms)
                       :last-error nil
                       :result ""
                       :result-value result)}))

(rf/reg-event-fx ::request-error
  (fn [{db :db} [_ result started-at-ms]]
    {:db (update-state db assoc
                       :pending? false
                       :started-at-ms nil
                       :duration-ms (elapsed-ms started-at-ms)
                       :last-error result
                       :result ""
                       :result-value result)}))

(rf/reg-event-fx ::request-failure
  (fn [{db :db} [_ failure started-at-ms]]
    {:db (update-state db assoc
                       :pending? false
                       :started-at-ms nil
                       :duration-ms (elapsed-ms started-at-ms)
                       :last-error failure
                       :result-value nil
                       :result (fail-message failure))}))

(defn- execute-request! []
  (rf/dispatch [::execute-request]))

(defn- submit-shortcut? [e]
  (and (= "Enter" (.-key e))
       (or (.-ctrlKey e)
           (.-metaKey e))))

(defn- on-params-key-down [e pending?]
  (when (and (submit-shortcut? e)
             (not pending?))
    (.preventDefault e)
    (execute-request!)))

(def ^:private field-class ["flex" "flex-col" "gap-1" "mb-2"])
(def ^:private field-header-class ["flex" "justify-between" "items-center" "gap-2"])
(def ^:private field-label-class ["font-bold" "text-sm"])
(def ^:private header-actions-class ["inline-flex" "gap-1" "items-center"])
(def ^:private duration-class ["text-xs" "text-gray-600"])
(def ^:private history-entry-class ["border" "border-solid" "border-ui-gray-204" "rounded-sm"
                                    "bg-ui-white" "p-2" "w-full" "text-left" "cursor-pointer"
                                    "hover:bg-ui-gray-238"])
(def ^:private history-title-class ["font-mono" "font-bold" "text-sm"])
(def ^:private history-meta-class ["text-xs" "text-gray-600"])
(def ^:private control-class ["border" "border-solid" "border-ui-gray-204" "rounded-sm" "bg-ui-white"
                              "text-sm" "leading-normal"])
(def ^:private button-class (into control-class ["px-2" "py-1" "font-sans" "cursor-pointer"
                                                 "disabled:cursor-default" "disabled:opacity-60"]))
(def ^:private input-class (into control-class ["box-border" "w-full" "font-mono" "px-2" "py-1"]))
(def ^:private small-input-class (conj input-class "h-8"))
(def ^:private params-textarea-class (conj input-class "h-[140px]" "resize-y"))
(def ^:private result-textarea-class (conj input-class "h-[260px]" "resize-y"))

(defn- labelled-field [label child]
  [:label {:class field-class}
   [:span {:class field-label-class} label]
   child])

(defn- labelled-field-with-actions [label actions child]
  [:div {:class field-class}
   [:div {:class field-header-class}
    [:span {:class field-label-class} label]
    actions]
   child])

(defn- result-format-button [format label selected?]
  [:button {:type     "button"
            :disabled selected?
            :on-click #(rf/dispatch [::set-result-format format])
            :class    (cond-> button-class
                        selected? (conj "font-bold"))}
   label])

(defn- params-format-button [format label selected?]
  [:button {:type     "button"
            :disabled selected?
            :on-click #(rf/dispatch [::convert-params format])
            :class    (cond-> button-class
                        selected? (conj "font-bold"))}
   label])

(defn- type-label [type]
  (case type
    "command" "Command"
    "query"   "Query"
    type))

(defn- format-timestamp [timestamp]
  (js/util.finnishDateAndTime (js/Date. timestamp)))

(defn- history-entry [{:keys [timestamp type action] :as entry}]
  [:button {:type     "button"
            :class    history-entry-class
            :on-click #(rf/dispatch [::restore-request entry])}
   [:span.flex.justify-between.items-start.gap-2
    [:span {:class history-title-class}
     (str (type-label type) ": " action)]
    [:span {:class history-meta-class}
     (format-timestamp timestamp)]]])

(defn- list-tab-button [tab label count selected?]
  [:button {:type     "button"
            :class    (cond-> button-class
                        selected? (conj "font-bold"))
            :on-click #(when-not selected?
                         (rf/dispatch [::set-selected-list-tab tab]))}
   (str label " (" count ")")])

(defn- request-lists [selected-list-tab history tracked-actions]
  (let [history-tab? (= "history" selected-list-tab)
        entries      (if history-tab? history tracked-actions)
        clear-event  (if history-tab? ::clear-history ::clear-tracked-actions)
        empty-text   (if history-tab?
                       "No requests yet."
                       "No tracked actions yet.")]
    [:div.mt-4
     [:div.flex.justify-between.items-center.gap-2.mb-2
      [:div.inline-flex.gap-1
       [list-tab-button "history" "Request history" (count history) history-tab?]
       [list-tab-button "tracking" "Action tracking" (count tracked-actions) (not history-tab?)]]
      [:button {:type     "button"
                :class    button-class
                :disabled (empty? entries)
                :on-click #(rf/dispatch [clear-event])}
       "Clear"]]
     (if (seq entries)
       (into [:div.flex.flex-col.gap-2]
             (map (fn [entry]
                    ^{:key (or (:tracking-id entry) (:hash entry))}
                    [history-entry entry])
                  entries))
       [:div {:class history-meta-class}
        empty-text])]))

(defn panel []
  (let [state (rf/subscribe [::state])]
    (fn []
      (let [{:keys [type action params params-format result-format pending? history duration-ms
                    track-page-actions? selected-list-tab tracked-actions]
             :as   state'}                                                                      @state]
        [:div.nobase.twp.lp-theme.px-2.pb-3.text-sm
         [:span.group-title.text-base.font-bold "Action request"]
         [:div.flex.gap-2
          [labelled-field
           "Type"
           [:select {:value     type
                     :class     small-input-class
                     :on-change #(rf/dispatch [::set-type (event-value %)])}
            [:option {:value "query"} "Query"]
            [:option {:value "command"} "Command"]]]
          [labelled-field
           "Name"
           [:input {:type      "text"
                    :value     action
                    :class     small-input-class
                    :on-change #(rf/dispatch [::set-action (event-value %)])}]]]
         [labelled-field-with-actions
          "Parameters"
          [:span {:class header-actions-class}
           [:button {:type     "button"
                     :class    button-class
                     :on-click #(rf/dispatch [::format-params])}
            "Format"]
           [params-format-button "json" "JSON" (= "json" params-format)]
           [params-format-button "cljs" "CLJS" (= "cljs" params-format)]]
          [:textarea {:value       params
                      :class       params-textarea-class
                      :on-change   #(rf/dispatch [::set-params (event-value %)])
                      :on-blur     #(rf/dispatch [::detect-params-format])
                      :on-key-down #(on-params-key-down % pending?)}]]
         [:div.flex.items-center.gap-2.mb-2
          [:button {:type     "button"
                    :disabled pending?
                    :class    button-class
                    :on-click execute-request!}
           (if pending? "Sending..." "Send")]]
         [labelled-field-with-actions
          "Result"
          [:span {:class header-actions-class}
           [result-format-button "json" "JSON" (= "json" result-format)]
           [result-format-button "cljs" "CLJS" (= "cljs" result-format)]]
          [:div.flex.flex-col.gap-1
           [:textarea {:read-only true
                       :value     (visible-result state')
                       :class     result-textarea-class}]
           (when-let [duration-ms duration-ms]
             [:div {:class duration-class}
              (str "Request time: " (format-duration duration-ms))])
           [:label.flex.items-center.gap-2
            [:input {:type      "checkbox"
                     :checked   (boolean track-page-actions?)
                     :on-change #(rf/dispatch [::set-track-page-actions (.. % -target -checked)])}]
            [:span "Track page action requests"]]]]
         [request-lists selected-list-tab history tracked-actions]]))))
